/**
 * CampanaDetallePage — Métricas de una campaña (ruta /notificaciones/:id, §8.3.3).
 *
 * Embudo en KPI cards + BarChart horizontal (recharts), timeline, metadata y
 * tabla de ejecuciones (subcolección envios) para recurrentes.
 */
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { orderBy } from 'firebase/firestore';
import {
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  Copy,
  Eye,
  FlaskConical,
  Info,
  Loader2,
  Megaphone,
  MousePointerClick,
  Pencil,
  Repeat,
  Send,
  Target,
  XCircle,
} from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import { Modal } from '../../components/ui/Modal';
import { useCollection, useDocument } from '../../hooks/useFirestore';
import { useToast } from '../../contexts/ToastContext';
import { cancelarCampanaFn, duplicarCampanaFn, mensajeError } from './notificacionesApi';
import { EnviarPruebaModal } from './EnviarPruebaModal';
import {
  CATALOGO_BOTONES,
  ESTADO_BADGE,
  ESTADO_LABELS,
  NOTA_BIGQUERY,
  TTL_OPCIONES,
  describirRecurrencia,
  fmtFechaHora,
  pctSeguro,
  type Campana,
  type EnvioCampana,
} from './tiposCampana';
import styles from './CampanaDetallePage.module.css';

// Colores de charts — mismos hex ya usados por AdminDashboardPage
const COLOR_PRIMARIO = '#0B6E37';
const COLOR_VERDE_MEDIO = '#2A9D5C';
const COLOR_WARNING = '#f59e0b';
const COLOR_ERROR = '#B42F2F';
const COLOR_VERDE_CLARO = '#4DB87A';

export default function CampanaDetallePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const { data: campana, loading } = useDocument<Campana>('campanas', id);

  const enviosConstraints = useMemo(() => [orderBy('ejecutadaEn', 'desc')], []);
  const { data: enviosRaw } = useCollection<EnvioCampana>(
    `campanas/${id ?? '_'}/envios`,
    enviosConstraints,
  );
  const envios = enviosRaw ?? [];

  const [confirmCancelar, setConfirmCancelar] = useState(false);
  const [modalPrueba, setModalPrueba] = useState(false);
  const [accionEnCurso, setAccionEnCurso] = useState(false);

  const stats = campana?.stats;

  const embudo = useMemo(() => {
    if (!stats) return null;
    const errores = stats.erroresToken + stats.erroresOtros;
    return [
      { name: 'Objetivo', value: stats.dispositivosObjetivo, color: COLOR_PRIMARIO },
      { name: 'Aceptadas', value: stats.aceptadosFcm, color: COLOR_VERDE_MEDIO },
      { name: 'Errores', value: errores, color: COLOR_ERROR },
      { name: 'Aperturas', value: stats.aperturas, color: COLOR_WARNING },
      { name: 'Vistas in-app', value: stats.vistasEnApp, color: COLOR_VERDE_CLARO },
    ];
  }, [stats]);

  const duplicar = async () => {
    if (!campana) return;
    setAccionEnCurso(true);
    try {
      const res = await duplicarCampanaFn({ campanaId: campana.id });
      addToast('success', 'Campaña duplicada como borrador.');
      navigate(`/notificaciones/${res.data.campanaId}/editar`);
    } catch (err) {
      addToast('error', mensajeError(err, 'No se pudo duplicar la campaña.'));
    } finally {
      setAccionEnCurso(false);
    }
  };

  const cancelar = async () => {
    if (!campana) return;
    setAccionEnCurso(true);
    try {
      await cancelarCampanaFn({ campanaId: campana.id });
      addToast('success', 'Campaña cancelada.');
      setConfirmCancelar(false);
    } catch (err) {
      addToast('error', mensajeError(err, 'No se pudo cancelar la campaña.'));
    } finally {
      setAccionEnCurso(false);
    }
  };

  if (loading) {
    return (
      <MainLayout pageTitle="Detalle de campaña" onBack={() => navigate('/notificaciones')}>
        <div className={styles.page}>
          <p className={styles.cargando}>
            <Loader2 size={16} className={styles.spin} aria-hidden="true" /> Cargando campaña…
          </p>
        </div>
      </MainLayout>
    );
  }

  if (!campana) {
    return (
      <MainLayout pageTitle="Detalle de campaña" onBack={() => navigate('/notificaciones')}>
        <div className={styles.page}>
          <EmptyState
            variant="error"
            title="Campaña no encontrada"
            message="La campaña no existe o fue eliminada."
            action={
              <Button variant="secondary" onClick={() => navigate('/notificaciones')}>
                Volver a campañas
              </Button>
            }
          />
        </div>
      </MainLayout>
    );
  }

  const editable = ['borrador', 'programada', 'recurrente'].includes(campana.estado);
  const cancelable = campana.estado === 'programada' || campana.estado === 'recurrente';
  const esRecurrente = !!campana.recurrencia;

  // ¿Ya hubo al menos un envío? Si no, las métricas en cero no significan
  // nada: se muestra un estado explicativo en lugar del embudo vacío.
  const fueEnviada =
    !!campana.enviadaEn ||
    campana.estado === 'enviando' ||
    !!(stats && (stats.dispositivosObjetivo > 0 || stats.aceptadosFcm > 0));

  const preEnvio = (() => {
    if (fueEnviada) return null;
    switch (campana.estado) {
      case 'borrador':
        return {
          titulo: 'Esta campaña es un borrador',
          texto:
            'Aún no se ha enviado. El embudo de entrega (objetivo, aceptadas, errores, aperturas y vistas) aparecerá aquí automáticamente después del envío.',
        };
      case 'programada':
        return {
          titulo: 'Campaña programada',
          texto: `Se enviará el ${fmtFechaHora(campana.programadaPara)}. Las métricas de entrega aparecerán aquí después del envío.`,
        };
      case 'recurrente':
        return {
          titulo: 'Campaña recurrente sin ejecuciones',
          texto: `${describirRecurrencia(campana.recurrencia)}. La primera ejecución será el ${fmtFechaHora(campana.recurrencia?.proximaEjecucion)} y sus métricas aparecerán aquí.`,
        };
      case 'cancelada':
        return {
          titulo: 'Campaña cancelada sin enviar',
          texto: 'Esta campaña se canceló antes de enviarse, por lo que no hay métricas de entrega. Puede duplicarla para reutilizar su contenido.',
        };
      case 'error':
        return {
          titulo: 'El envío falló antes de iniciar',
          texto: 'No se registraron envíos. Duplique la campaña e intente de nuevo; si el problema persiste revise la auditoría.',
        };
      default:
        return {
          titulo: 'Sin métricas todavía',
          texto: 'Las métricas de entrega aparecerán aquí después del envío.',
        };
    }
  })();

  return (
    <MainLayout
      pageTitle="Detalle de campaña"
      pageSubtitle={campana.nombre}
      onBack={() => navigate('/notificaciones')}
    >
      <div className={styles.page}>
        {/* ─── Header ─── */}
        <div className={styles.header}>
          <div className={styles.headerInfo}>
            {campana.imagenUrl ? (
              <img src={campana.imagenUrl} alt="" className={styles.headerThumb} />
            ) : (
              <span className={`${styles.headerThumb} ${styles.headerThumbPlaceholder}`} aria-hidden="true">
                <Megaphone size={18} />
              </span>
            )}
            <div>
              <div className={styles.headerTituloRow}>
                <h2 className={styles.headerTitulo}>{campana.titulo}</h2>
                <Badge variant={ESTADO_BADGE[campana.estado]} size="sm">
                  <span className={styles.badgeInner}>
                    {campana.estado === 'recurrente' && <Repeat size={11} aria-hidden="true" />}
                    {campana.estado === 'enviando' && (
                      <Loader2 size={11} className={styles.spin} aria-hidden="true" />
                    )}
                    {ESTADO_LABELS[campana.estado]}
                  </span>
                </Badge>
              </div>
              <p className={styles.headerDesc}>{campana.descripcion}</p>
            </div>
          </div>
          <div className={styles.headerAcciones}>
            {editable && (
              <Button variant="secondary" size="sm" onClick={() => setModalPrueba(true)}>
                <FlaskConical size={14} aria-hidden="true" /> Enviar a prueba
              </Button>
            )}
            {editable && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate(`/notificaciones/${campana.id}/editar`)}
              >
                <Pencil size={14} aria-hidden="true" /> Editar
              </Button>
            )}
            <Button variant="secondary" size="sm" loading={accionEnCurso} onClick={() => void duplicar()}>
              <Copy size={14} aria-hidden="true" /> Duplicar
            </Button>
            {cancelable && (
              <Button variant="danger" size="sm" onClick={() => setConfirmCancelar(true)}>
                <XCircle size={14} aria-hidden="true" /> Cancelar
              </Button>
            )}
          </div>
        </div>

        {/* ─── Contenido de la campaña (lo que recibe el paciente) ─── */}
        <div className={styles.contenidoCard}>
          <div className={styles.contenidoHead}>
            <h3 className={styles.contenidoTitle}>
              <Info size={15} aria-hidden="true" /> Contenido de la campaña
            </h3>
          </div>
          <div className={styles.contenidoBody}>
            {campana.imagenUrl && (
              <a href={campana.imagenUrl} target="_blank" rel="noreferrer" className={styles.contenidoImgWrap}>
                <img src={campana.imagenUrl} alt="Imagen de la campaña" className={styles.contenidoImg} />
              </a>
            )}
            <div className={styles.contenidoMeta}>
              <div className={styles.contenidoField}>
                <span className={styles.contenidoLabel}>Título</span>
                <span className={styles.contenidoValue}>{campana.titulo || '—'}</span>
              </div>
              <div className={styles.contenidoField}>
                <span className={styles.contenidoLabel}>Descripción</span>
                <span className={styles.contenidoValue}>{campana.descripcion || '—'}</span>
              </div>
              {campana.linkMasInfo && (
                <div className={styles.contenidoField}>
                  <span className={styles.contenidoLabel}>Enlace «más información»</span>
                  <a href={campana.linkMasInfo} target="_blank" rel="noreferrer" className={styles.contenidoLink}>
                    {campana.linkMasInfo}
                  </a>
                </div>
              )}
              {campana.botones && campana.botones.length > 0 && (
                <div className={styles.contenidoField}>
                  <span className={styles.contenidoLabel}>Botones de acción</span>
                  <div className={styles.contenidoChips}>
                    {campana.botones.map((b) => (
                      <span key={b.id} className={styles.contenidoChip}>
                        {CATALOGO_BOTONES.find((c) => c.id === b.id)?.label ?? b.id}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div className={styles.contenidoInline}>
                <div className={styles.contenidoField}>
                  <span className={styles.contenidoLabel}>Prioridad</span>
                  <span className={styles.contenidoValue}>
                    {campana.prioridad === 'alta' ? 'Alta' : 'Normal'}
                  </span>
                </div>
                <div className={styles.contenidoField}>
                  <span className={styles.contenidoLabel}>Vigencia (TTL)</span>
                  <span className={styles.contenidoValue}>
                    {TTL_OPCIONES.find((t) => t.value === campana.ttlHoras)?.label ?? `${campana.ttlHoras} h`}
                  </span>
                </div>
                {campana.enviadaEn && (
                  <div className={styles.contenidoField}>
                    <span className={styles.contenidoLabel}>Enviada</span>
                    <span className={styles.contenidoValue}>{fmtFechaHora(campana.enviadaEn)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ─── Embudo: KPI cards (solo si ya hubo envío) ─── */}
        {fueEnviada && stats && (
          <div className={styles.kpiGrid}>
            <div className={styles.kpiCard}>
              <div className={styles.kpiHeader}>
                <p className={styles.kpiLabel}>Objetivo</p>
                <span className={`${styles.kpiIcon} ${styles.kpiIconPrimary}`}>
                  <Target size={15} aria-hidden="true" />
                </span>
              </div>
              <p className={styles.kpiValue}>
                {stats.dispositivosObjetivo.toLocaleString('es-PA')}
              </p>
              <p className={styles.kpiMeta}>Dispositivos al momento del envío</p>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiHeader}>
                <p className={styles.kpiLabel}>Aceptadas</p>
                <span className={`${styles.kpiIcon} ${styles.kpiIconPrimary}`}>
                  <CheckCircle2 size={15} aria-hidden="true" />
                </span>
              </div>
              <p className={styles.kpiValue}>{stats.aceptadosFcm.toLocaleString('es-PA')}</p>
              <p className={styles.kpiMeta}>
                {pctSeguro(stats.aceptadosFcm, stats.dispositivosObjetivo)} del objetivo
              </p>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiHeader}>
                <p className={styles.kpiLabel}>Errores</p>
                <span className={`${styles.kpiIcon} ${styles.kpiIconError}`}>
                  <AlertTriangle size={15} aria-hidden="true" />
                </span>
              </div>
              <p className={styles.kpiValue}>
                {(stats.erroresToken + stats.erroresOtros).toLocaleString('es-PA')}
              </p>
              <p className={styles.kpiMeta}>
                {stats.erroresToken.toLocaleString('es-PA')} token ·{' '}
                {stats.erroresOtros.toLocaleString('es-PA')} otros
              </p>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiHeader}>
                <p className={styles.kpiLabel}>Aperturas</p>
                <span className={`${styles.kpiIcon} ${styles.kpiIconWarning}`}>
                  <MousePointerClick size={15} aria-hidden="true" />
                </span>
              </div>
              <p className={styles.kpiValue}>{stats.aperturas.toLocaleString('es-PA')}</p>
              <p className={styles.kpiMeta}>
                {pctSeguro(stats.aperturas, stats.aceptadosFcm)} de aceptadas
              </p>
            </div>
            <div className={styles.kpiCard}>
              <div className={styles.kpiHeader}>
                <p className={styles.kpiLabel}>Vistas in-app</p>
                <span className={`${styles.kpiIcon} ${styles.kpiIconInfo}`}>
                  <Eye size={15} aria-hidden="true" />
                </span>
              </div>
              <p className={styles.kpiValue}>{stats.vistasEnApp.toLocaleString('es-PA')}</p>
              <p className={styles.kpiMeta}>
                {pctSeguro(stats.vistasEnApp, stats.aperturas)} de aperturas
              </p>
            </div>
          </div>
        )}

        <div className={styles.contenido}>
          <div className={styles.colPrincipal}>
            {/* ─── Estado pre-envío: sin métricas que mostrar ─── */}
            {preEnvio && (
              <div className={styles.card}>
                <div className={styles.preEnvio}>
                  <span className={styles.preEnvioIcono} aria-hidden="true">
                    {campana.estado === 'programada' ? (
                      <CalendarClock size={22} />
                    ) : campana.estado === 'recurrente' ? (
                      <Repeat size={22} />
                    ) : (
                      <Send size={22} />
                    )}
                  </span>
                  <h3 className={styles.preEnvioTitulo}>{preEnvio.titulo}</h3>
                  <p className={styles.preEnvioTexto}>{preEnvio.texto}</p>
                  {campana.estado === 'borrador' && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => navigate(`/notificaciones/${campana.id}/editar`)}
                    >
                      <Pencil size={14} aria-hidden="true" /> Completar y enviar
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* ─── Gráfico de embudo ─── */}
            {fueEnviada && embudo && (
              <div className={styles.card}>
                <h3 className={styles.cardTitulo}>Embudo de entrega</h3>
                <div className={styles.chartWrap}>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={embudo} layout="vertical" margin={{ left: 8, right: 24 }}>
                      <XAxis type="number" hide />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={100}
                        tickLine={false}
                        axisLine={false}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip
                        formatter={(value) => [
                          Number(value ?? 0).toLocaleString('es-PA'),
                          'Dispositivos',
                        ]}
                        cursor={{ fill: 'rgba(11, 110, 55, 0.06)' }}
                      />
                      <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={22}>
                        {embudo.map((item) => (
                          <Cell key={item.name} fill={item.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className={styles.notaFija}>
                  <Info size={14} aria-hidden="true" />
                  <span>{NOTA_BIGQUERY}</span>
                </div>
              </div>
            )}

            {/* ─── Envíos (recurrentes) ─── */}
            {esRecurrente && (
              <div className={styles.card}>
                <h3 className={styles.cardTitulo}>
                  Ejecuciones{' '}
                  {campana.recurrencia?.enviosRealizados !== undefined
                    ? `(${campana.recurrencia.enviosRealizados})`
                    : ''}
                </h3>
                {envios.length === 0 ? (
                  <p className={styles.vacio}>Aún no se ha ejecutado ningún envío.</p>
                ) : (
                  <div className={styles.tableScroll}>
                    <table className={styles.table}>
                      <thead>
                        <tr>
                          <th>Ejecutada</th>
                          <th>Objetivo</th>
                          <th>Aceptadas</th>
                          <th>Errores</th>
                        </tr>
                      </thead>
                      <tbody>
                        {envios.map((e) => (
                          <tr key={e.id}>
                            <td>{fmtFechaHora(e.ejecutadaEn)}</td>
                            <td>{e.stats?.dispositivosObjetivo?.toLocaleString('es-PA') ?? '—'}</td>
                            <td>
                              {e.stats?.aceptadosFcm?.toLocaleString('es-PA') ?? '—'}{' '}
                              <span className={styles.pctInline}>
                                ({pctSeguro(e.stats?.aceptadosFcm ?? 0, e.stats?.dispositivosObjetivo ?? 0)})
                              </span>
                            </td>
                            <td>
                              {((e.stats?.erroresToken ?? 0) + (e.stats?.erroresOtros ?? 0)).toLocaleString('es-PA')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className={styles.colLateral}>
            {/* ─── Timeline ─── */}
            <div className={styles.card}>
              <h3 className={styles.cardTitulo}>Historial</h3>
              <ol className={styles.timeline}>
                <li className={styles.timelineItem}>
                  <span className={`${styles.timelineDot} ${styles.dotOk}`} aria-hidden="true" />
                  <div>
                    <p className={styles.timelineTitulo}>Creada</p>
                    <p className={styles.timelineMeta}>
                      {fmtFechaHora(campana.creadoEn)} · {campana.creadoPorNombre}
                    </p>
                  </div>
                </li>
                {campana.programadaPara && (
                  <li className={styles.timelineItem}>
                    <span className={`${styles.timelineDot} ${styles.dotInfo}`} aria-hidden="true">
                      <CalendarClock size={10} />
                    </span>
                    <div>
                      <p className={styles.timelineTitulo}>Programada</p>
                      <p className={styles.timelineMeta}>{fmtFechaHora(campana.programadaPara)}</p>
                    </div>
                  </li>
                )}
                {campana.recurrencia && (
                  <li className={styles.timelineItem}>
                    <span className={`${styles.timelineDot} ${styles.dotInfo}`} aria-hidden="true">
                      <Repeat size={10} />
                    </span>
                    <div>
                      <p className={styles.timelineTitulo}>Recurrencia</p>
                      <p className={styles.timelineMeta}>
                        {describirRecurrencia(campana.recurrencia)}
                        {campana.recurrencia.proximaEjecucion && (
                          <> · próximo: {fmtFechaHora(campana.recurrencia.proximaEjecucion)}</>
                        )}
                      </p>
                    </div>
                  </li>
                )}
                {campana.enviadaEn && (
                  <li className={styles.timelineItem}>
                    <span className={`${styles.timelineDot} ${styles.dotOk}`} aria-hidden="true">
                      <Send size={10} />
                    </span>
                    <div>
                      <p className={styles.timelineTitulo}>Enviada</p>
                      <p className={styles.timelineMeta}>{fmtFechaHora(campana.enviadaEn)}</p>
                    </div>
                  </li>
                )}
                {campana.estado === 'cancelada' && (
                  <li className={styles.timelineItem}>
                    <span className={`${styles.timelineDot} ${styles.dotError}`} aria-hidden="true">
                      <XCircle size={10} />
                    </span>
                    <div>
                      <p className={styles.timelineTitulo}>Cancelada</p>
                    </div>
                  </li>
                )}
              </ol>
            </div>

            {/* ─── Metadata ─── */}
            <div className={styles.card}>
              <h3 className={styles.cardTitulo}>Configuración</h3>
              <dl className={styles.meta}>
                <div className={styles.metaFila}>
                  <dt>Creada por</dt>
                  <dd>{campana.creadoPorNombre}</dd>
                </div>
                <div className={styles.metaFila}>
                  <dt>Vigencia hasta</dt>
                  <dd>{fmtFechaHora(campana.vigenciaHasta)}</dd>
                </div>
                <div className={styles.metaFila}>
                  <dt>TTL del push</dt>
                  <dd>
                    {TTL_OPCIONES.find((o) => o.value === campana.ttlHoras)?.label ??
                      `${campana.ttlHoras} h`}
                  </dd>
                </div>
                <div className={styles.metaFila}>
                  <dt>Prioridad</dt>
                  <dd>{campana.prioridad === 'alta' ? 'Alta' : 'Normal'}</dd>
                </div>
                {campana.linkMasInfo && (
                  <div className={styles.metaFila}>
                    <dt>Link</dt>
                    <dd className={styles.metaLink}>
                      <a href={campana.linkMasInfo} target="_blank" rel="noreferrer">
                        {campana.linkMasInfo}
                      </a>
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Enviar a usuarios de prueba ─── */}
      <EnviarPruebaModal
        open={modalPrueba}
        onClose={() => setModalPrueba(false)}
        campanaId={campana.id}
      />

      {/* ─── Confirmar cancelación ─── */}
      <Modal
        open={confirmCancelar}
        onClose={() => setConfirmCancelar(false)}
        title="Cancelar campaña"
      >
        <div className={styles.confirmBody}>
          <p className={styles.confirmTexto}>
            ¿Desea cancelar la campaña <strong>{campana.nombre}</strong>?{' '}
            {campana.estado === 'recurrente'
              ? 'No se realizarán más envíos recurrentes.'
              : 'No se enviará en la fecha programada.'}
          </p>
          <div className={styles.confirmAcciones}>
            <Button variant="secondary" onClick={() => setConfirmCancelar(false)}>
              Volver
            </Button>
            <Button variant="danger" loading={accionEnCurso} onClick={() => void cancelar()}>
              Sí, cancelar campaña
            </Button>
          </div>
        </div>
      </Modal>
    </MainLayout>
  );
}
